import { flowPathId, smoothStep } from './geometry'
import { anchorFor, type GraphEdge, type GraphGroup, type GraphNode } from './types'

interface Props {
  nodes: GraphNode[]
  edges: GraphEdge[]
  groups?: GraphGroup[]
  isMobile: boolean
  visited: Set<string>
}

/**
 * Bottom layer: one invisible motion path per edge (the tracer dots ride these)
 * plus the visible connectors.
 *
 * `vectorEffect="non-scaling-stroke"` is load-bearing — the canvas uses
 * preserveAspectRatio="none", so without it every stroke would thicken
 * horizontally as the viewBox stretches.
 */
export function EdgeLayer({ nodes, edges, groups, isMobile, visited }: Props) {
  const drawable = edges.filter((e) => !e.internal)

  return (
    <svg
      className="tl-edges absolute inset-0 h-full w-full"
      viewBox="0 0 100 100"
      preserveAspectRatio="none"
      aria-hidden="true"
    >
      <defs>
        {drawable.map((e) => {
          const a = anchorFor(e.from, nodes, groups, isMobile)
          const b = anchorFor(e.to, nodes, groups, isMobile)
          if (!a || !b) return null
          return (
            <path
              key={flowPathId(e.from, e.to)}
              id={flowPathId(e.from, e.to)}
              d={smoothStep(a.x, a.y, b.x, b.y)}
              fill="none"
            />
          )
        })}
      </defs>

      {drawable.map((e) => {
        const a = anchorFor(e.from, nodes, groups, isMobile)
        const b = anchorFor(e.to, nodes, groups, isMobile)
        if (!a || !b) return null

        const lit = visited.has(e.from) && visited.has(e.to)

        // Four edge styles, in precedence order.
        const cls = e.activeOnly
          ? 'tl-edge-subtle'
          : e.solid
            ? 'tl-edge-output'
            : lit
              ? 'tl-edge-active'
              : 'tl-edge-resting'

        return (
          <path
            key={`${e.from}-${e.to}`}
            d={smoothStep(a.x, a.y, b.x, b.y)}
            fill="none"
            className={cls}
            vectorEffect="non-scaling-stroke"
            strokeLinecap="round"
            style={{
              opacity: lit ? undefined : 0.05,
              // Dashes only march once both ends are lit — an unvisited lane
              // sits still rather than animating in the dark.
              animationPlayState: lit ? 'running' : 'paused',
            }}
          />
        )
      })}
    </svg>
  )
}
